import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY, patchAppUser } from "./appAuthSlice";
import { isPendingRoundActive } from "../hooks/usePendingPlayCountdown";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const apiMessage = (error, fallback) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  error.message ||
  fallback;

const adjustCoinBalance = (dispatch, getState, delta) => {
  const current = Number(getState?.()?.appAuth?.user?.COIN || 0);
  if (!Number.isFinite(current)) return;
  dispatch(patchAppUser({ COIN: Math.max(0, current + delta) }));
};

export const fetchCoinFlipConstants = createAsyncThunk(
  "coinFlip/fetchConstants",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/coin-flip/constants");
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load Coin Flip rules"));
    }
  }
);

export const fetchCoinFlipLanding = createAsyncThunk(
  "coinFlip/fetchLanding",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/coin-flip/landing");
      return res.data?.pendingRound || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load pending flip"));
    }
  }
);

export const fetchCoinFlipPool = createAsyncThunk(
  "coinFlip/fetchPool",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/coin-flip/pool");
      return res.data || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load investor pool"));
    }
  }
);

export const fetchCoinFlipHistory = createAsyncThunk(
  "coinFlip/fetchHistory",
  async ({ limit = 20 } = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/coin-flip/rounds/history", { params: { limit } });
      return res.data?.rounds || [];
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load flip history"));
    }
  }
);

export const fetchCoinFlipInvestors = createAsyncThunk(
  "coinFlip/fetchInvestors",
  async ({ limit = 100 } = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/coin-flip/investors", { params: { limit } });
      return res.data?.investors || [];
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load investors"));
    }
  }
);

export const fetchCoinFlipPlayers = createAsyncThunk(
  "coinFlip/fetchPlayers",
  async ({ limit = 100 } = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/coin-flip/players", { params: { limit } });
      return res.data?.players || [];
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load players"));
    }
  }
);

export const fetchCoinFlipQuote = createAsyncThunk(
  "coinFlip/fetchQuote",
  async ({ stake }, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/coin-flip/quote", { params: { stake } });
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Invalid stake"));
    }
  }
);

export const coinFlipBuy = createAsyncThunk(
  "coinFlip/buy",
  async ({ playerSide, stake }, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/coin-flip/buy", { playerSide, stake });
      const round = res.data?.round || null;
      if (round?.stake) adjustCoinBalance(dispatch, getState, -Number(round.stake));
      dispatch(fetchCoinFlipPool());
      dispatch(fetchCoinFlipInvestors({ limit: 100 }));
      dispatch(fetchCoinFlipPlayers({ limit: 100 }));
      return round;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not reserve flip"));
    }
  }
);

export const coinFlipPlay = createAsyncThunk(
  "coinFlip/play",
  async ({ roundId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/coin-flip/flip", { roundId });
      const round = res.data?.round || null;
      if (round?.status === "Won") {
        const net = Number(round.playerNetPayout || 0);
        if (net > 0) adjustCoinBalance(dispatch, getState, net);
      }
      dispatch(fetchCoinFlipPool());
      dispatch(fetchCoinFlipInvestors({ limit: 100 }));
      dispatch(fetchCoinFlipPlayers({ limit: 100 }));
      return round;
    } catch (error) {
      const msg = apiMessage(error, "Could not flip coin");
      if (/expired/i.test(msg)) {
        const stake = Number(getState?.()?.coinFlip?.pendingRound?.stake || 0);
        if (stake > 0) adjustCoinBalance(dispatch, getState, stake);
      }
      return rejectWithValue(msg);
    }
  }
);

export const coinFlipCancel = createAsyncThunk(
  "coinFlip/cancel",
  async ({ roundId, stake = 0 }, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/coin-flip/cancel", { roundId });
      if (stake > 0) adjustCoinBalance(dispatch, getState, Number(stake));
      dispatch(fetchCoinFlipPool());
      dispatch(fetchCoinFlipInvestors({ limit: 100 }));
      dispatch(fetchCoinFlipPlayers({ limit: 100 }));
      return res.data?.round || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not cancel flip"));
    }
  }
);

export const coinFlipInvest = createAsyncThunk(
  "coinFlip/invest",
  async ({ amount }, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/coin-flip/invest", { amount });
      const invested = Number(res.data?.amount || 0);
      if (invested > 0) adjustCoinBalance(dispatch, getState, -invested);
      dispatch(fetchCoinFlipPool());
      dispatch(fetchCoinFlipInvestors({ limit: 100 }));
      dispatch(fetchCoinFlipPlayers({ limit: 100 }));
      return res.data || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Invest failed"));
    }
  }
);

export const coinFlipInvestorCashout = createAsyncThunk(
  "coinFlip/investorCashout",
  async ({ amount } = {}, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/coin-flip/investor/cashout", { amount });
      const withdrawn = Number(res.data?.amount || 0);
      if (withdrawn > 0) adjustCoinBalance(dispatch, getState, withdrawn);
      dispatch(fetchCoinFlipPool());
      dispatch(fetchCoinFlipInvestors({ limit: 100 }));
      dispatch(fetchCoinFlipPlayers({ limit: 100 }));
      return res.data || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Cash out failed"));
    }
  }
);

export const coinFlipInvestorToggleActive = createAsyncThunk(
  "coinFlip/investorToggleActive",
  async ({ isActive }, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/coin-flip/investor/toggle-active", { isActive });
      return res.data || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not update investor status"));
    }
  }
);

const initialState = {
  constants: null,
  pendingRound: null,
  lastSettledRound: null,
  pool: null,
  players: [],
  investors: [],
  history: [],
  quote: null,
  loading: false,
  loadingPool: false,
  loadingPlayers: false,
  loadingInvestors: false,
  actionLoading: false,
  investorActionLoading: false,
  error: null,
};

const coinFlipSlice = createSlice({
  name: "coinFlip",
  initialState,
  reducers: {
    setCoinFlipPendingRound: (state, { payload }) => {
      state.pendingRound = payload || null;
    },
    setCoinFlipLastSettled: (state, { payload }) => {
      state.lastSettledRound = payload || null;
    },
    clearCoinFlipPendingRound: (state) => {
      state.pendingRound = null;
    },
    clearCoinFlipLastSettled: (state) => {
      state.lastSettledRound = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCoinFlipConstants.fulfilled, (state, { payload }) => {
        state.constants = payload;
      })
      .addCase(fetchCoinFlipLanding.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCoinFlipLanding.fulfilled, (state, { payload }) => {
        state.loading = false;
        const ttlMs = state.constants?.pendingPlayTtlMs || 60 * 1000;
        state.pendingRound =
          payload?.status === "Pending" && isPendingRoundActive(payload, ttlMs) ? payload : null;
      })
      .addCase(fetchCoinFlipLanding.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(fetchCoinFlipPool.fulfilled, (state, { payload }) => {
        state.pool = payload;
        state.loadingPool = false;
      })
      .addCase(fetchCoinFlipPlayers.pending, (state) => {
        state.loadingPlayers = true;
      })
      .addCase(fetchCoinFlipPlayers.fulfilled, (state, { payload }) => {
        state.players = payload || [];
        state.loadingPlayers = false;
      })
      .addCase(fetchCoinFlipPlayers.rejected, (state) => {
        state.loadingPlayers = false;
      })
      .addCase(fetchCoinFlipInvestors.pending, (state) => {
        state.loadingInvestors = true;
      })
      .addCase(fetchCoinFlipInvestors.fulfilled, (state, { payload }) => {
        state.investors = payload || [];
        state.loadingInvestors = false;
      })
      .addCase(fetchCoinFlipInvestors.rejected, (state) => {
        state.loadingInvestors = false;
      })
      .addCase(fetchCoinFlipQuote.fulfilled, (state, { payload }) => {
        state.quote = payload;
      })
      .addCase(coinFlipBuy.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(coinFlipBuy.fulfilled, (state, { payload }) => {
        state.actionLoading = false;
        state.pendingRound = payload;
      })
      .addCase(coinFlipBuy.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(coinFlipPlay.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(coinFlipPlay.fulfilled, (state, { payload }) => {
        state.actionLoading = false;
        state.pendingRound = null;
        state.lastSettledRound = payload;
      })
      .addCase(coinFlipPlay.rejected, (state, { payload }) => {
        state.actionLoading = false;
        if (typeof payload === "string" && /expired/i.test(payload)) {
          state.pendingRound = null;
        }
      })
      .addCase(coinFlipCancel.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(coinFlipCancel.fulfilled, (state) => {
        state.actionLoading = false;
        state.pendingRound = null;
      })
      .addCase(coinFlipCancel.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(coinFlipInvest.pending, (state) => {
        state.investorActionLoading = true;
      })
      .addCase(coinFlipInvest.fulfilled, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(coinFlipInvest.rejected, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(coinFlipInvestorCashout.pending, (state) => {
        state.investorActionLoading = true;
      })
      .addCase(coinFlipInvestorCashout.fulfilled, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(coinFlipInvestorCashout.rejected, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(coinFlipInvestorToggleActive.pending, (state) => {
        state.investorActionLoading = true;
      })
      .addCase(coinFlipInvestorToggleActive.fulfilled, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(coinFlipInvestorToggleActive.rejected, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(fetchCoinFlipHistory.fulfilled, (state, { payload }) => {
        state.history = payload || [];
      });
  },
});

export const {
  setCoinFlipPendingRound,
  setCoinFlipLastSettled,
  clearCoinFlipPendingRound,
  clearCoinFlipLastSettled,
} = coinFlipSlice.actions;

export const selectCoinFlipConstants = (state) => state.coinFlip?.constants;
export const selectCoinFlipPendingRound = (state) => state.coinFlip?.pendingRound;
export const selectCoinFlipLastSettled = (state) => state.coinFlip?.lastSettledRound;
export const selectCoinFlipPool = (state) => state.coinFlip?.pool;
export const selectCoinFlipPlayers = (state) => state.coinFlip?.players || [];
export const selectCoinFlipInvestors = (state) => state.coinFlip?.investors || [];
export const selectCoinFlipLoadingPlayers = (state) => state.coinFlip?.loadingPlayers;
export const selectCoinFlipLoadingInvestors = (state) => state.coinFlip?.loadingInvestors;
export const selectCoinFlipQuote = (state) => state.coinFlip?.quote;
export const selectCoinFlipLoading = (state) => state.coinFlip?.loading;
export const selectCoinFlipActionLoading = (state) => state.coinFlip?.actionLoading;
export const selectCoinFlipInvestorActionLoading = (state) => state.coinFlip?.investorActionLoading;
export const selectCoinFlipHistory = (state) => state.coinFlip?.history || [];

export default coinFlipSlice.reducer;
